<template lang="pug">
content-page
	div(slot="nav")
		navigation
	div.hidden-sm-and-down(slot="sidebar")
		ad(location="sidebar").mb-6
		offers.mb-6
		snow-alarm

	h1 {{contentpage.name}}
	img.ml-2(v-if="$i18n.locale !== 'de-DE'" :alt="$t('page.weather:roel.title')" src="https://static.wintersport.nl/images/roel_trans.png" style="float: right;")
	ws-content(:value="contentpage.content")
		template(slot="weblog" v-if="weblog")
			h2.mt-6.mb-1 {{weblog.title}}
			.subtitle-1.grey--text.mb-1 {{$t('page.weather:weatherreport.{date}.title', {date: $dayjs(weblog.publishDate).format('dddd D MMMM')})}}
			v-container.pa-0
				v-row.mb-6
					v-col(cols="12" xl="7")
						v-img(:src="`${weblog.photo}?width=840`" :aspect-ratio="16/9")
					v-col
						p.mb-0 {{weblog.content.introduction}}
						.text-center
							v-btn.mt-3(color="primary" outlined :to="localePath({name: 'weblog-slug', params: { 0: weblog.slug}})" :title="weblog.title") {{$t('page.weather:weatherreport.read_full_report.button')}}

		template(slot="webloglist" v-if="weblogList")
			v-simple-table.mb-6
				tbody
					tr.py-2.py-md-0(v-for="blog in weblogList" :key="blog.id" @click.capture="$router.push(localePath({name: 'weblog-slug', params: { 0: blog.slug}}))")
						td.pl-0
							ws-icon.pr-1.success--text(small) weblog
							nuxt-link(:to="localePath({name: 'weblog-slug', params: { 0: blog.slug}})" :title="blog.title") {{blog.title}}
							.grey--text.caption.hidden-md-and-up {{blog.publishDate|date}}
						td.text-right.hidden-sm-and-down.text-no-wrap {{blog.publishDate|date}}
						td
							ws-icon.pr-1.grey--text(size="20") comments
							| {{blog.replyCount}}

		template(slot="snowforecast")
			v-container.mb-6
				v-row
					v-col(style="width:90px")
					v-col.text-center.font-weight-bold.caption(v-for="(day, index) in forecastDays" :key="`head-${index}`" :class="{'hidden-xs-only': index >= 3}") {{$tc('page.weather:thumbs.heading_{count}_{date}', index, {count: index, date: day.format('dd D MMM')})}}
				v-row.mb-3(align="center" no-gutters)
					v-col.font-weight-bold.caption(style="width:90px") {{$t('page.weather:thumbs.snowfall')}}
					v-col.pl-1(v-for="(day, index) in forecastDays" :key="`snow-${index}`" :class="{'hidden-xs-only': index >= 3}")
						nuxt-link(:to="localePath({name: 'weather-type', params: {type: $localeSlug('snowfall', $i18n.locale)}, hash: `#${(index+1)*24}`})", :title="$t('page.weather:thumbs.snowfall_for_{date}.title', {date: day.format('dd D MMM')})")
							region-map(mini :hour="(index+1)*24" type="snowfall")
				v-row.mb-3(align="center" no-gutters)
					v-col.font-weight-bold.caption(style="width:90px") {{$t('page.weather:thumbs.sun')}}
					v-col.pl-1(v-for="(day, index) in forecastDays" :key="`sun-${index}`" :class="{'hidden-xs-only': index >= 3}")
						nuxt-link(:to="localePath({name: 'weather-type', params: {type: $localeSlug('sun', $i18n.locale)}, hash: `#${(index+1)*24}`})", :title="$t('page.weather:thumbs.sun_for_{date}.title', {date: day.format('dd D MMM')})")
							region-map(mini :hour="(index+1)*24" type="sun")
				v-row.mb-3(align="center" no-gutters)
					v-col.font-weight-bold.caption(style="width:90px") {{$t('page.weather:thumbs.temperature')}}
					v-col.pl-1(v-for="(day, index) in forecastDays" :key="`temp-${index}`" :class="{'hidden-xs-only': index >= 3}")
						nuxt-link(:to="localePath({name: 'weather-type', params: {type: $localeSlug('temperature', $i18n.locale)}, hash: `#${index * 24 + 12}`})", :title="$t('page.weather:thumbs.temperature_for_{date}.title', {date: day.format('dd D MMM')})")
							region-map(mini :hour="index * 24 + 12" type="temperature")

		template(slot="webcams")
			webcam-list.mb-3.pa-0(:items="areas")
			.text-center.mb-6
				v-btn(color="primary" outlined nuxt :to="localePath({name: 'weather-webcams'})" :title="$t('nav.webcams.title')") {{ $tc('page.weather:webcams.{count}.button', webcamCount, {count: webcamCount}) }}

		template(slot="snowheight")
			snowheight-list-table.mb-3(:snowheights="snowheightsForCountry" :limit="10")
			.text-center.mb-6
				v-btn(color="primary" outlined nuxt :to="localePath({name: 'weather-snowheight'})" :title="$t('snowheight_table.button_top_100.title')") {{ $t('snowheight_table.button_top_100') }}

	snow-alarm.hidden-md-and-up
</template>

<script>
import gql from 'graphql-tag';
import { mapState } from 'vuex';
import Content from '@dengl/common/components/content.vue';
import Navigation from '@/components/navigation';
import WebcamList from '@/components/webcam-list';
import SnowheightListTable from '@/components/weather/snowheight-list-table';
import MetaFragment from '@/components/graphql/metaFragment.graphql';

import RegionMap from '@/components/weather/weathermap/regionmap.vue';

export default {
	name: 'WeatherIndex',
	nuxtI18n: {
		paths: {
			'nl-NL': '/weersneeuw',
			'de-DE': '/wetter-schnee',
			'nl-BE': '/weersneeuw'
		}
	},
	components: {
		Navigation,
		WebcamList,
		SnowheightListTable,
		RegionMap
	},
	layout: 'default',

	asyncData({ app, route }) {
		app.ads({ sidebar: { visible: true } });

		return app.$graphClient
			.query({
				query: gql`
					query getMainpageData($areaSlugs: [String], $path: String) {
						weblogItems: weblogsByCategory(limit: 6, category: "weather") {
							weblogs {
								id
								title
								slug
								content {
									introduction(length: 240)
								}
								publishDate
								url
								photo
								replyCount
								author {
									id
									username
									signature
									teamDescription
									role
								}
							}
						}
						webcamCount: totalWebcamCount
						snowheightnodes: areas {
							id
							name
							slug
							country {
								id
								name
								countryCode
							}
							snowheights {
								maxSnowValley
								maxSnowMountain
								nodes {
									lastSnowAt
									snowLast24
									slopeLengthOpen
								}
							}
						}
						areas(slugs: $areaSlugs) {
							name
							slug
							stats {
								webcamCount
							}
							webcamLarge {
								name
								id
								preview
							}
							webcamSmall {
								name
								id
								preview
							}
						}
						contentpage(path: $path) {
							id
							name
							slug
							content {
								...ContentDataFragment
							}
							...MetaFragment
						}
					}
					${MetaFragment}
					${Content.fragments}
				`,
				variables: {
					areaSlugs: [
						'skiwelt',
						'silvretta-montafon',
						'winterberg',
						'la-plagne',
						'portesdusoleil',
						'valgardena'
					],
					path: route.path
				},
				errorPolicy: 'all'
			})
			.then(({ data, errors }) => {
				if (errors) {
					throw errors[0];
				}

				app.meta(data.contentpage.meta);
				app.breadcrumbs({
					[app.i18n.t('page.weather:breadcrumb')]: { name: 'weather' }
				});

				return data;
			})
			.catch(error => {
				return app.httpError(500, null, 'weather-01', error);
			});
	},

	computed: {
		snowheightsForCountry() {
			let nodes = this.snowheightnodes;

			// remove items without snow
			nodes = nodes.filter(
				node =>
					node.snowheights.maxSnowMountain > 0 ||
					node.snowheights.maxSnowValley > 0
			);

			// sort by maxSnow
			nodes.sort(function (a, b) {
				return b.snowheights.maxSnowMountain - a.snowheights.maxSnowMountain;
			});

			// create flat object
			nodes = nodes.map((node, index) => {
				// get ranking with classes
				const rankColors = ['gold', 'silver', 'bronze'];
				const ranking = index + 1;
				const rankClass = ranking - 1 > 2 ? ['black--text'] : ['white--text'];
				rankClass.push(rankColors[ranking - 1] || 'grey lighten-3');

				// find snowLast24
				let snowheights = [...node.snowheights.nodes];
				const snowLast24 = snowheights.length
					? snowheights.reduce((prev, curr) => {
							return prev.snowLast24 < curr.snowLast24 ? prev : curr;
					  }).snowLast24
					: null;

				// find snowLast24
				snowheights = [...node.snowheights.nodes];
				const lastSnowAt = snowheights.length
					? snowheights.reduce((prev, curr) => {
							return this.$dayjs(prev.lastSnowAt).isBefore(
								this.$dayjs(curr.lastSnowAt)
							)
								? prev
								: curr;
					  }).lastSnowAt
					: null;

				// find slopeLengthOpen
				snowheights = [...node.snowheights.nodes];
				const slopeLengthOpen = snowheights.length
					? snowheights.reduce((prev, curr) => {
							return prev.slopeLengthOpen < curr.slopeLengthOpen ? prev : curr;
					  }).slopeLengthOpen
					: 0;

				return {
					id: node.id,
					name: node.name,
					slug: node.slug,
					country: node.country,
					ranking,
					rankClass,
					snowMountain: node.snowheights.maxSnowMountain,
					snowValley: node.snowheights.maxSnowValley,
					snowLast24,
					lastSnowAt,
					slopeLengthOpen
				};
			});

			return nodes;
		},

		...mapState(['rundate']),
		weblog() {
			return this.weblogItems.weblogs[0];
		},
		weblogList() {
			return this.weblogItems.weblogs.slice(1, 6);
		},
		forecastDays() {
			return Array(6)
				.fill(0)
				.map((_e, i) => {
					return this.$dayjs(this.rundate).add(i, 'd');
				});
		}
	},
	head() {
		return {
			...this.$structuredData()
		};
	}
};
</script>

<style lang="scss">
.intro :after {
	clear: both;
	display: table;
	height: 0;
}
</style>
